import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Users, ArrowUpRight, Loader2, Award, HandCoins, Heart, ShieldAlert, AlertTriangle, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MasterTransactions() {
  const [data, setData] = useState(null);
  const [founding, setFounding] = useState(null);
  const [stripeFeed, setStripeFeed] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('masterToken') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API}/master/transactions`, { headers }),
          fetch(`${API}/master/founding-revenue`, { headers }),
        ]);
        if (r1.ok) setData(await r1.json());
        if (r2.ok) setFounding(await r2.json());
      } catch {} finally { setLoading(false); }
    })();
    // Iter 198l — pull the LIVE Stripe feed too so we see charges that
    // never made it into our DB (card testing, direct API abuse, etc.)
    (async () => {
      try {
        const r = await fetch(`${API}/master/stripe/live-charges?days=30`, { headers });
        if (r.ok) setStripeFeed(await r.json());
      } catch {} finally { setStripeLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;
  if (!data) return <div className="text-center text-[#9CA3AF] py-20">Failed to load transactions</div>;

  return (
    <div className="space-y-6" data-testid="master-transactions">
      <div>
        <h1 className="text-2xl font-bold text-white font-syne flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center"><DollarSign size={20} /></div>
          Transactions / Indigenous Businesses Supported
        </h1>
        <p className="text-[#9CA3AF] text-sm mt-1">Track all marketplace transactions and your platform revenue</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-5">
          <p className="text-[#64748B] text-xs mb-1">Total Transaction Volume</p>
          <p className="text-white font-mono font-bold text-3xl">${data.summary?.total_volume?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-green-500/10 rounded-2xl p-5">
          <p className="text-[#64748B] text-xs mb-1">Your Platform Fees (10%)</p>
          <p className="text-green-400 font-mono font-bold text-3xl">${data.summary?.total_platform_fees?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-5">
          <p className="text-[#64748B] text-xs mb-1">Total Transactions</p>
          <p className="text-[#A78BFA] font-mono font-bold text-3xl">{data.summary?.total_transactions || 0}</p>
        </div>
      </div>

      {/* ─────────────────────── Founding Programs Revenue ─────────────────────── */}
      {founding && (
        <div className="space-y-4" data-testid="founding-revenue-section">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white font-syne flex items-center gap-2">
              <Award size={18} className="text-amber-400" /> Founding Programs Revenue
            </h2>
            <span className="text-[11px] text-[#64748B] font-mono">
              {founding.summary.founding_vendor_paid_count + founding.summary.founding_nonprofit_paid_count} paid seats · ${founding.summary.combined_revenue.toFixed(2)} total raised
            </span>
          </div>

          {/* Three program stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20 rounded-2xl p-5" data-testid="fv-revenue-card">
              <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-wider mb-2">
                <HandCoins size={14} /> Founding Vendors
              </div>
              <p className="text-white font-mono font-bold text-3xl">${founding.summary.founding_vendor_revenue.toFixed(2)}</p>
              <p className="text-[#9CA3AF] text-xs mt-2">
                <span className="text-amber-400 font-semibold">{founding.summary.founding_vendor_seats_filled}</span> / 50 seats · {founding.summary.founding_vendor_seats_remaining} left · $150 each
              </p>
              <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-500 to-yellow-500 transition-all" style={{ width: `${(founding.summary.founding_vendor_seats_filled / 50) * 100}%` }} />
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 rounded-2xl p-5" data-testid="np-revenue-card">
              <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-wider mb-2">
                <Heart size={14} /> Founding Non-Profits
              </div>
              <p className="text-white font-mono font-bold text-3xl">${founding.summary.founding_nonprofit_revenue.toFixed(2)}</p>
              <p className="text-[#9CA3AF] text-xs mt-2">
                <span className="text-emerald-400 font-semibold">{founding.summary.founding_nonprofit_seats_filled}</span> / 10 seats · {founding.summary.founding_nonprofit_seats_remaining} left · $2,500 each
              </p>
              <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all" style={{ width: `${(founding.summary.founding_nonprofit_seats_filled / 10) * 100}%` }} />
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#8B5CF6]/15 to-[#3B82F6]/10 border border-[#8B5CF6]/30 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-[#A78BFA] text-xs uppercase tracking-wider mb-2">
                <TrendingUp size={14} /> Combined Founding Revenue
              </div>
              <p className="text-white font-mono font-bold text-3xl">${founding.summary.combined_revenue.toFixed(2)}</p>
              <p className="text-[#9CA3AF] text-xs mt-2">
                Theoretical max: <span className="text-white font-semibold">${(50 * 150 + 10 * 2500).toLocaleString()}</span>
              </p>
              <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] transition-all" style={{ width: `${Math.min(100, (founding.summary.combined_revenue / 32500) * 100)}%` }} />
              </div>
            </div>
          </div>

          {/* Purchase log */}
          {(founding.founding_vendors.length > 0 || founding.founding_nonprofits.length > 0) ? (
            <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                <p className="text-[#C4B5FD] text-xs uppercase tracking-wider font-bold">Founding purchases log</p>
                <p className="text-[#64748B] text-xs">Most recent first</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="founding-purchases-table">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left text-[#64748B] font-medium p-4 text-xs">Date</th>
                      <th className="text-left text-[#64748B] font-medium p-4 text-xs">Program</th>
                      <th className="text-left text-[#64748B] font-medium p-4 text-xs">#</th>
                      <th className="text-left text-[#64748B] font-medium p-4 text-xs">Business</th>
                      <th className="text-left text-[#64748B] font-medium p-4 text-xs">Owner</th>
                      <th className="text-left text-[#64748B] font-medium p-4 text-xs">Email</th>
                      <th className="text-right text-[#64748B] font-medium p-4 text-xs">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ...founding.founding_vendors.map((r) => ({ ...r, program: 'founding_vendor' })),
                      ...founding.founding_nonprofits.map((r) => ({ ...r, program: 'founding_nonprofit' })),
                    ]
                      .sort((a, b) => (b.paid_at || '').localeCompare(a.paid_at || ''))
                      .map((r) => (
                        <tr key={r.purchase_id || r.session_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="p-4 text-[#9CA3AF] text-xs font-mono whitespace-nowrap">
                            {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${
                              r.program === 'founding_vendor'
                                ? 'bg-amber-500/15 text-amber-300'
                                : 'bg-emerald-500/15 text-emerald-300'
                            }`}>
                              {r.program === 'founding_vendor' ? 'Founding Vendor' : 'Founding Non-Profit'}
                            </span>
                          </td>
                          <td className="p-4 text-white font-bold font-mono">#{r.founding_number || '?'}</td>
                          <td className="p-4 text-white">{r.business_name || '—'}</td>
                          <td className="p-4 text-[#C4B5FD]">{r.owner_name || '—'}</td>
                          <td className="p-4 text-[#9CA3AF] text-xs">{r.email || '—'}</td>
                          <td className="p-4 text-right font-mono font-bold text-green-400">${(r.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-[#1E1E3A]/40 rounded-2xl border border-white/5">
              <p className="text-[#64748B] text-sm italic">No founding purchases yet — you'll see them here and get an email the moment a seat sells.</p>
            </div>
          )}
        </div>
      )}

      {/* Transaction List */}
      {data.transactions?.length === 0 ? (
        <div className="text-center py-16 bg-[#1E1E3A]/80 rounded-2xl border border-white/5">
          <DollarSign size={48} className="mx-auto text-[#64748B] mb-4" />
          <p className="text-[#9CA3AF]">No transactions yet</p>
          <p className="text-[#64748B] text-sm">Transactions will appear here when customers book vendor services</p>
        </div>
      ) : (
        <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-[#64748B] font-medium p-4 text-xs">Consumer</th>
                  <th className="text-left text-[#64748B] font-medium p-4 text-xs"><ArrowUpRight size={12} className="inline mr-1" />Vendor</th>
                  <th className="text-right text-[#64748B] font-medium p-4 text-xs">Amount</th>
                  <th className="text-right text-[#64748B] font-medium p-4 text-xs">Your Fee (10%)</th>
                  <th className="text-right text-[#64748B] font-medium p-4 text-xs">Vendor Gets</th>
                  <th className="text-left text-[#64748B] font-medium p-4 text-xs">Type</th>
                  <th className="text-left text-[#64748B] font-medium p-4 text-xs">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map(txn => (
                  <tr key={txn.transaction_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`txn-${txn.transaction_id}`}>
                    <td className="p-4">
                      <p className="text-white font-medium">{txn.consumer_name}</p>
                      <p className="text-[#64748B] text-[10px]">{txn.consumer_email}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-[#A78BFA] font-medium">{txn.vendor_name}</p>
                      <p className="text-[#64748B] text-[10px]">{txn.vendor_email}</p>
                    </td>
                    <td className="p-4 text-right text-white font-mono">${txn.amount?.toFixed(2)}</td>
                    <td className="p-4 text-right text-green-400 font-mono font-bold">${txn.platform_fee?.toFixed(2)}</td>
                    <td className="p-4 text-right text-[#9CA3AF] font-mono">${txn.vendor_payout?.toFixed(2)}</td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${txn.type === 'ad_boost' ? 'bg-[#F59E0B]/15 text-[#FCD34D]' : txn.type === 'subscription' ? 'bg-[#8B5CF6]/15 text-[#A78BFA]' : 'bg-green-500/15 text-green-400'}`}>
                        {txn.type?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="p-4 text-[#64748B] text-xs">{txn.paid_at ? new Date(txn.paid_at).toLocaleDateString() : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Iter 198l — LIVE STRIPE FEED. Shows EVERY charge on the Stripe
          account, including card-testing attempts + charges that bypassed
          our normal checkout flow. Unmatched successes are red-flagged. */}
      <div className="bg-[#141019] border border-white/10 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/40 to-orange-500/40 border border-red-400/30 flex items-center justify-center">
              <ShieldAlert size={18} className="text-red-300" />
            </div>
            <div>
              <div className="text-white text-base font-bold">Live Stripe feed · last 30 days</div>
              <div className="text-white/50 text-xs">Every charge on your Stripe account. Unmatched = didn't flow through our checkout.</div>
            </div>
          </div>
          {stripeFeed && (
            <div className="text-right">
              <div className="text-white text-xs">
                <span className="text-emerald-300 font-bold">{stripeFeed.counts.succeeded}</span> succeeded
                <span className="mx-1.5 text-white/25">·</span>
                <span className="text-red-300 font-bold">{stripeFeed.counts.suspected_card_test}</span> flagged
              </div>
              <div className="text-white/40 text-[10px] uppercase tracking-wider">
                Gross ${(stripeFeed.counts.gross_cents/100).toFixed(2)}
              </div>
            </div>
          )}
        </div>
        {stripeLoading ? (
          <div className="py-10 text-center text-white/40"><Loader2 size={20} className="animate-spin inline mr-2" /> Loading Stripe…</div>
        ) : !stripeFeed ? (
          <div className="py-10 text-center text-white/40">Could not load Stripe feed.</div>
        ) : stripeFeed.charges.length === 0 ? (
          <div className="py-10 text-center text-white/40">No charges in the last 30 days.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="stripe-live-feed">
              <thead className="bg-white/[0.03] border-b border-white/8">
                <tr className="text-white/40 text-[10px] uppercase tracking-[0.14em] font-bold">
                  <th className="text-left px-4 py-2.5">When</th>
                  <th className="text-left px-4 py-2.5">Amount</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Name / Email</th>
                  <th className="text-left px-4 py-2.5">Card</th>
                  <th className="text-left px-4 py-2.5">Country</th>
                  <th className="text-left px-4 py-2.5">Matched</th>
                  <th className="text-left px-4 py-2.5">Flags</th>
                </tr>
              </thead>
              <tbody>
                {stripeFeed.charges.map((c) => {
                  const flagged = c.flags.length > 0;
                  const rowCls = c.flags.includes('unmatched_success') && c.flags.length >= 2
                    ? 'bg-red-500/[0.06] hover:bg-red-500/[0.10]'
                    : flagged
                    ? 'bg-amber-500/[0.04] hover:bg-amber-500/[0.08]'
                    : 'hover:bg-white/[0.03]';
                  return (
                    <tr key={c.charge_id} className={`border-b border-white/[0.04] ${rowCls}`} data-testid={`stripe-charge-${c.charge_id}`}>
                      <td className="px-4 py-2.5 text-white/60 text-xs whitespace-nowrap">
                        {new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-2.5 text-white font-bold tabular-nums whitespace-nowrap">{c.amount_display}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold border ${
                          c.status === 'succeeded' ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
                          : c.status === 'failed'  ? 'text-red-300 border-red-400/40 bg-red-500/10'
                          : 'text-white/50 border-white/15 bg-white/5'
                        }`}>{c.status}</span>
                      </td>
                      <td className="px-4 py-2.5 min-w-0">
                        <div className="text-white/80 text-xs truncate max-w-[200px]">{c.billing_name || '—'}</div>
                        <div className="text-white/40 text-[10px] truncate max-w-[200px]">{c.billing_email || '—'}</div>
                      </td>
                      <td className="px-4 py-2.5 text-white/60 text-xs whitespace-nowrap">
                        {c.card_brand ? `${c.card_brand} …${c.card_last4}` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-white/60 text-xs whitespace-nowrap">
                        <span title="Billing country">{c.billing_country || '—'}</span>
                        <span className="text-white/25 mx-1">/</span>
                        <span title="Card issuer country" className={c.flags.includes('card_country_mismatch') ? 'text-amber-300 font-bold' : ''}>{c.card_country || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        {c.matched ? (
                          <span className="text-emerald-300">{c.matched.kind === 'invoice' ? 'Invoice' : 'Order'}</span>
                        ) : c.status === 'succeeded' ? (
                          <span className="text-red-300 font-bold">Unmatched</span>
                        ) : (
                          <span className="text-white/30">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {c.flags.map(f => (
                            <span key={f} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-red-500/15 text-red-300 border border-red-400/30" title={f}>
                              <AlertTriangle size={9} /> {f.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {c.receipt_url && (
                            <a href={c.receipt_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-white/40 hover:text-white/70">
                              <ExternalLink size={9} /> Receipt
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
